import { action, createStore } from "easy-peasy";

/** Plan key for cart line (e.g. day, week, month, lifetime). */
export type PlanKey = string;

export interface CartLineItem {
  productId: string;
  quantity: number;
  plan?: PlanKey;
}

function cartLineKey(productId: string, plan?: PlanKey): string {
  return plan != null ? `${productId}:${plan}` : `${productId}:single`;
}

type CartState = { items: CartLineItem[]; isLoaded: boolean };

const cartModel = {
  items: [] as CartLineItem[],
  isLoaded: false,

  setLoaded: action((state: CartState, payload: boolean) => {
    state.isLoaded = payload;
  }),

  hydrateCart: action((state: CartState, payload: CartLineItem[]) => {
    state.items = payload;
    state.isLoaded = true;
  }),

  addToCart: action((state: CartState, payload: {
    productId: string;
    quantity: number;
    plan?: PlanKey;
  }) => {
    const key = cartLineKey(payload.productId, payload.plan);
    const existing = state.items.find(
      (item) => cartLineKey(item.productId, item.plan) === key
    );
    if (existing) {
      existing.quantity = Math.max(1, existing.quantity + payload.quantity);
    } else {
      state.items.push({
        productId: payload.productId,
        quantity: Math.max(1, payload.quantity),
        plan: payload.plan,
      });
    }
  }),

  updateQuantity: action((state: CartState, payload: {
    productId: string;
    quantity: number;
    plan?: PlanKey;
  }) => {
    const key = cartLineKey(payload.productId, payload.plan);
    const index = state.items.findIndex(
      (item) => cartLineKey(item.productId, item.plan) === key
    );
    if (index === -1) return;
    if (payload.quantity <= 0) {
      state.items.splice(index, 1);
    } else {
      state.items[index]!.quantity = payload.quantity;
    }
  }),

  removeFromCart: action((state: CartState, payload: {
    productId: string;
    plan?: PlanKey;
  }) => {
    const key = cartLineKey(payload.productId, payload.plan);
    const index = state.items.findIndex(
      (item) => cartLineKey(item.productId, item.plan) === key
    );
    if (index !== -1) state.items.splice(index, 1);
  }),

  clearCart: action((state: CartState) => {
    state.items = [];
  }),
};

export interface StoreModel {
  cart: {
    items: CartLineItem[];
    isLoaded: boolean;
    setLoaded: (loaded: boolean) => void;
    hydrateCart: (items: CartLineItem[]) => void;
    addToCart: (payload: {
      productId: string;
      quantity: number;
      plan?: PlanKey;
    }) => void;
    updateQuantity: (payload: {
      productId: string;
      quantity: number;
      plan?: PlanKey;
    }) => void;
    removeFromCart: (payload: { productId: string; plan?: PlanKey }) => void;
    clearCart: () => void;
  };
}

const storeModel: StoreModel = {
  cart: cartModel as unknown as StoreModel["cart"],
};

export const store = createStore<StoreModel>(storeModel, {
  name: "forge-store",
});

/** Get cart line for a product (and optional plan). */
export function getCartLine(
  items: CartLineItem[],
  productId: string,
  plan?: PlanKey
): CartLineItem | undefined {
  const key = cartLineKey(productId, plan);
  return items.find((item) => cartLineKey(item.productId, item.plan) === key);
}
